import React from 'react';
import { ChevronRight } from 'lucide-react';
import type { Fixture } from '../../services/TennisLiveApi';
import { TennisMatchCard } from './TennisMatchCard';

interface TournamentGroupProps {
  tournamentName: string;
  fixtures: Fixture[];
  onMatchClick: (fixture: Fixture) => void;
  onTournamentClick?: () => void;
}

const CATEGORY_ORDER = [
  'Simples Masculino',
  'Simples Feminino',
  'Duplas Masculinas',
  'Duplas Femininas',
  'Duplas Mistas',
  'Copa por Equipes',
  'Masculino Juvenil',
  'Feminino Juvenil',
  'Outros'
];

export const TournamentGroup: React.FC<TournamentGroupProps> = ({ tournamentName, fixtures, onMatchClick, onTournamentClick }) => {
  // Group by matchCategory
  const categories = fixtures.reduce((acc, fix) => {
    const cat = fix.matchCategory || 'Outros';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(fix);
    return acc;
  }, {} as Record<string, Fixture[]>);

  const formatCategoryName = (c: string) => {
    const dict: Record<string, string> = {
      'Simples Masculino': "MEN'S SINGLES",
      'Simples Feminino': "WOMEN'S SINGLES",
      'Duplas Masculinas': "MEN'S DOUBLES",
      'Duplas Femininas': "WOMEN'S DOUBLES",
      'Duplas Mistas': "MIXED DOUBLES",
      'Copa por Equipes': "TEAM CUP",
      'Masculino Juvenil': "BOYS' SINGLES",
      'Feminino Juvenil': "GIRLS' SINGLES",
      'Outros': "OTHER"
    };
    return dict[c] || c.toUpperCase();
  };

  const sortedCategories = Object.keys(categories).sort((a, b) => {
    const indexA = CATEGORY_ORDER.indexOf(a);
    const indexB = CATEGORY_ORDER.indexOf(b);
    const orderA = indexA === -1 ? 999 : indexA;
    const orderB = indexB === -1 ? 999 : indexB;
    return orderA - orderB;
  });

  return (
    <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', overflow: 'hidden', marginBottom: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
      {/* Header */}
      <div 
        onClick={onTournamentClick}
        style={{ 
          padding: '16px 20px', 
          borderBottom: '1px solid #e2e8f0', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          cursor: onTournamentClick ? 'pointer' : 'default'
        }}
      >
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#0f172a' }}>{tournamentName}</h2>
        {onTournamentClick && <ChevronRight size={20} color="#3b82f6" />}
      </div>

      <div style={{ padding: '0 20px' }}>
        {sortedCategories.map(cat => (
          <div key={cat} style={{ padding: '16px 0', borderBottom: '1px solid #f1f5f9' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '12px', fontWeight: 'bold', color: '#475569' }}>
              {formatCategoryName(cat)}
            </h3>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
              gap: '16px' 
            }}>
              {categories[cat].map(fixture => (
                <TennisMatchCard key={fixture.id} fixture={fixture} onClick={() => onMatchClick(fixture)} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
